import { IConfigs } from "@ts/common-types";

export const stopStream = (ref: any) => {
    if (ref) {
        ref.abort();
        ref = null;
    }
};

export async function startEventStreamWithPost(
    endpoint: string,
    requestConfig: IConfigs,
    body: Record<string, any>,
    onMessage: (data: any) => void,
    onClose?: () => void,
    onError?: (err: any) => void,
    signal?: AbortSignal
) {
    try {
        const response = await fetch(endpoint, {
            ...requestConfig,
            body: JSON.stringify(body),
            signal: signal,
        });

        if (!response.body) {
            throw new Error("No response body");
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder("utf-8");
        let buffer = "";

        // eslint-disable-next-line no-constant-condition
        while (true) {
            const { value, done } = await reader.read();
            if (done) {
                onClose?.();
                break;
            }

            const chunk = decoder.decode(value, { stream: true });
            buffer += chunk;

            const messages = buffer.split("\n\n");

            // Leave the last chunk (may be incomplete)
            buffer = messages.pop() || "";

            for (const msg of messages) {
                const lines = msg.split("\n").filter(Boolean);
                const dataLine = lines.find((line) => line.startsWith("data:"));
                if (dataLine) {
                    const dataContent = dataLine.slice(5).trim();
                    //!Note: This is an additional check, we expect stream data to be a defined object in any case
                    if (
                        dataContent &&
                        dataContent !== "undefined" &&
                        dataContent !== "null" &&
                        dataContent !== ""
                    ) {
                        try {
                            const payload = JSON.parse(dataContent);
                            onMessage(payload);
                        } catch {
                            onMessage(dataContent);
                        }
                    }
                }
            }
        }
    } catch (err) {
        onError?.(err);
        onClose?.();
    }
}

export const request = async (
    config: IConfigs,
    streamingOptions?: {
        isStreaming: boolean;
        onMessage: (data: any) => void;
        onClose?: () => void;
        onError?: (err: any) => void;
        signal?: AbortSignal;
    }
) => {
    try {
        const baseUrl = `${config.API_DOMAIN || "/api"}/${config.type}`;
        const dateQueryParams = new URLSearchParams();

        if (config.data && (config.data.dateRangeKey || config.data.start)) {
            if (config.data.dateRangeKey) {
                dateQueryParams.append(
                    "dateRangeKey",
                    config.data.dateRangeKey
                );
                delete config.data.dateRangeKey;
            } else {
                const { start, end } = config.data;
                dateQueryParams.append("start", start);
                dateQueryParams.append("end", end);
                delete config.data.start;
                delete config.data.end;
            }
        }
        const url = dateQueryParams.size
            ? new URL(`${baseUrl}?${dateQueryParams.toString()}`)
            : new URL(baseUrl);
        config.method = config.method || "post";

        if (
            config.data &&
            Object.keys(config.data).length &&
            config.method.toLowerCase() === "get"
        ) {
            url.search = new URLSearchParams(config.data).toString();
        }

        let data;
        const methodLower = (config.method || "get").toLowerCase();

        if (methodLower === "get") {
            data = undefined;
        } else if (config.requestType !== "multipart/form-data") {
            data = {
                ...(config.data || {}),
                ...(config.type === "rules" &&
                    config.data &&
                    config.data.filters && {
                        filters: JSON.parse(config.data.filters),
                    }),
            };
        } else {
            // Check if config.data is already a FormData object
            if (config.data instanceof FormData) {
                data = config.data;
            } else {
                const formData = new FormData();
                if (config.data && typeof config.data === "object") {
                    Object.keys(config.data).forEach((key) => {
                        const value = (config.data as any)[key];
                        if (value instanceof File) {
                            formData.append(key, value);
                        } else if (value !== null && value !== undefined) {
                            if (typeof value === "object") {
                                formData.append(key, JSON.stringify(value));
                            } else {
                                formData.append(key, String(value));
                            }
                        }
                    });
                }
                data = formData;
            }
        }

        const requestConfig: IConfigs = {
            method: config.method.toUpperCase(),
            headers: {
                ...config.headers,
                ...(config.requestType !== "multipart/form-data" && {
                    "Content-Type": config.requestType,
                }),
                ...(config.client && { "REV-API-SCOPE": config.client }),
                ...(config.channel && { CHANNEL: config.channel }),
                ...(config.country && { COUNTRY: config.country }),
                ...(config.module && { MODULE: config.module }),
                ...(config.clientGroupId && {
                    "CLIENT-GROUP": config.clientGroupId,
                }),
                ...(config.clientSubGroupId && {
                    "CLIENT-SUB-GROUP": config.clientSubGroupId,
                }),
                ...(config.revstackChannel && {
                    "REVSTACK-CHANNEL": Array.isArray(config.revstackChannel)
                        ? config.revstackChannel.join(",")
                        : config.revstackChannel,
                }),
                ...(config.cube_role && {
                    "X-ROLE": config.cube_role,
                }),
                TIMEZONE: config.timezone,
            },
            ...((config.method.toLowerCase() === "post" ||
                config.method.toLowerCase() === "put" ||
                config.method.toLowerCase() === "patch") && {
                body:
                    config.requestType === "application/json"
                        ? JSON.stringify(data)
                        : data,
            }),
            ...(streamingOptions?.signal
                ? { signal: streamingOptions.signal }
                : {}),
        };

        if (streamingOptions?.isStreaming) {
            return await startEventStreamWithPost(
                url.pathname + url.search,
                requestConfig,
                config.data || {},
                streamingOptions.onMessage,
                streamingOptions.onClose,
                streamingOptions.onError,
                streamingOptions.signal
            );
        }
        /* @ts-ignore */
        const response = await fetch(url, requestConfig);

        const contentType = response.headers.get("content-type") || "";
        const isJsonResponse = contentType.includes("application/json");
        const responseWithStatusCode = {
            response:
                isJsonResponse && !config.isExcelUpload
                    ? await response.json()
                    : response,
            statusCode: response.status,
        };

        return responseWithStatusCode;
    } catch (err) {
        console.error(err);
    }
};

onmessage = async (event) => postMessage(await request(event.data));
